import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { mediaQueryLarge, requestIdleCallback, startViewTransition } from '@theme/utilities';

/**
 * A custom element that renders a paginated results list using button-based navigation
 * and smooth view transition ajax section morphs.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 *
 * @extends Component<Refs>
 */
export default class ResultsList extends Component {
  /**
   * Called when the element is connected to the document's DOM.
   * Registers event listeners for layout changes and pagination click interception.
   */
  connectedCallback() {
    super.connectedCallback();

    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange);
    this.addEventListener('click', this.#handlePaginationClick);
    this.setAttribute('initialized', '');
  }

  /**
   * Called when the element is disconnected from the document's DOM.
   * Cleans up registered event listeners.
   */
  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#handleMediaQueryChange);
    this.removeEventListener('click', this.#handlePaginationClick);
  }

  /**
   * Gets the section ID from the element's attributes.
   *
   * @returns {string} The section ID.
   */
  get sectionId() {
    const id = this.getAttribute('section-id');
    if (!id) throw new Error('The section-id attribute is required');
    return id;
  }

  /**
   * Updates the layout grid view density.
   *
   * @param {object} param0
   * @param {EventTarget} param0.target
   */
  updateLayout({ target }) {
    if (!(target instanceof HTMLInputElement)) return;

    this.#animateLayoutChange(target.value);
  }

  /**
   * Intercepts clicks on pagination links, prevents default page reloads, and trigger smooth AJAX section updates.
   *
   * @param {MouseEvent} event - The click event.
   */
  #handlePaginationClick = (event) => {
    const link = event.target.closest('.pagination__link');
    if (!link) return;

    event.preventDefault();
    const url = new URL(link.href);

    // Update the history state so that page state matches the new page
    window.history.pushState({}, '', url.toString());

    // Update the section content via Section Rendering API
    this.#updateSection();
  };

  /**
   * Fetches the new section content, morphs the DOM, and smoothly scrolls to the results.
   */
  #updateSection = async () => {
    // Perform the update using standard view transition and section morphing
    await startViewTransition(async () => {
      await sectionRenderer.renderSection(this.sectionId);
    }, ['product-grid']);

    // Scroll page smoothly to the top of the ResultsList component
    this.scrollIntoView({ behavior: 'auto' });
  };

  /**
   * Animates the layout change.
   *
   * @param {string} value
   */
  #animateLayoutChange = async (value) => {
    const { grid } = this.refs;

    if (!grid) return;

    await startViewTransition(() => this.#setLayout(value), ['product-grid']);

    requestIdleCallback(() => {
      const viewport = mediaQueryLarge.matches ? 'desktop' : 'mobile';
      sessionStorage.setItem(`product-grid-view-${viewport}`, value);
    });
  };

  /**
   * Sets the layout attributes on the grid element.
   *
   * @param {string} value
   */
  #setLayout(value) {
    const { grid } = this.refs;
    if (!grid) return;
    grid.setAttribute('product-grid-view', value);
  }

  /**
   * Handles the media query change event.
   *
   * @param {MediaQueryListEvent} event
   */
  #handleMediaQueryChange = (event) => {
    const targetElement = event.matches
      ? this.querySelector('[data-grid-layout="desktop-default-option"]')
      : this.querySelector('[data-grid-layout="mobile-option"]');

    if (!(targetElement instanceof HTMLInputElement)) return;

    targetElement.checked = true;
    this.#setLayout('default');
  };
}

if (!customElements.get('results-list')) {
  customElements.define('results-list', ResultsList);
}
